import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Per-item detail PDF exports for food-cost module.
 * Each function generates a single-page PDF with all data for one item.
 * Opens in a new browser tab.
 */

// ── Shared helpers ────────────────────────────────────────────────────────

const MARGIN = 14;

// ── Arabic font loading (Amiri) ──────────────────────────────────────────

/** Cached base64 font data — loaded once, reused across PDF generations */
let amiriFontBase64 = null;

/**
 * Load the Amiri Arabic font and register it with a jsPDF document.
 * Font is fetched from public/fonts/ on first call, then cached in memory.
 */
async function loadArabicFont(doc) {
  if (!amiriFontBase64) {
    const response = await fetch("/fonts/Amiri-Regular.ttf");
    const buffer = await response.arrayBuffer();
    // Convert ArrayBuffer to base64 string for jsPDF
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    amiriFontBase64 = btoa(binary);
  }
  // Register the font with this document instance
  doc.addFileToVFS("Amiri-Regular.ttf", amiriFontBase64);
  doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
}

/** Check if a string contains Arabic characters */
function hasArabic(text) {
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text || "");
}

/** Create an A4 jsPDF doc with Arabic font pre-loaded */
async function createDoc(landscape = false) {
  const doc = new jsPDF({ orientation: landscape ? "landscape" : "portrait", unit: "mm", format: "a4" });
  await loadArabicFont(doc);
  return doc;
}

/** Add standard header: company name + title + date */
function addHeader(doc, title) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Pizza Felice LLC", MARGIN, MARGIN);
  doc.setFont("helvetica", "normal");
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  doc.text(dateStr, pageWidth - MARGIN, MARGIN, { align: "right" });

  // Title
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(title, MARGIN, MARGIN + 10);
  doc.setFont("helvetica", "normal");
  return MARGIN + 16;
}

/** Add footer with page numbers, timestamp, and confidential watermark on all pages */
function addFooter(doc) {
  const totalPages = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const now = new Date();
  const dateStr = now.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const timeStr = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Diagonal "INTERNAL — CONFIDENTIAL" watermark
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.06 }));
    doc.setFontSize(54);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    const cx = pageWidth / 2;
    const cy = pageHeight / 2;
    doc.text("INTERNAL \u2014 CONFIDENTIAL", cx, cy, { align: "center", angle: 35 });
    doc.restoreGraphicsState();

    // Page footer text
    doc.setFontSize(8);
    doc.setTextColor(130, 130, 130);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated on ${dateStr} at ${timeStr}`, MARGIN, pageHeight - 6);
    doc.text(`Page ${i} / ${totalPages}`, pageWidth - MARGIN, pageHeight - 6, { align: "right" });
  }
  doc.setTextColor(0, 0, 0);
}

/** Open PDF in new browser tab */
function openPdf(doc) {
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
}

/** Format number, return "-" if falsy */
function fmt(val, decimals = 2) {
  if (val === null || val === undefined || val === "") return "-";
  return Number(val).toFixed(decimals);
}

/** Print a label: value pair, return updated y. Switches to Amiri font for Arabic values. */
function printField(doc, label, value, y) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  // Check page break
  if (y + 6 > pageHeight - 14) { doc.addPage(); y = MARGIN; }
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text(`${label}:`, MARGIN, y);
  doc.setFont("helvetica", "normal");
  const labelWidth = doc.getTextWidth(`${label}: `);
  const valStr = String(value ?? "-");
  // Use Amiri font for Arabic text, Helvetica for Latin
  if (hasArabic(valStr)) {
    doc.setFont("Amiri", "normal");
    doc.text(valStr, MARGIN + labelWidth + 1, y);
    doc.setFont("helvetica", "normal");
  } else {
    doc.text(valStr, MARGIN + labelWidth + 1, y);
  }
  return y + 5;
}

/** Print a section title, return updated y */
function printSection(doc, title, y) {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + 10 > pageHeight - 14) { doc.addPage(); y = MARGIN; }
  y += 3;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(title, MARGIN, y);
  doc.setFont("helvetica", "normal");
  return y + 6;
}

// ── Ingredient Detail PDF ─────────────────────────────────────────────────

/**
 * Generate a detail PDF for a single ingredient.
 * @param {Object} item - Ingredient object with all fields
 */
export async function exportIngredientDetailPdf(item) {
  if (!item) return;

  const doc = await createDoc();
  let y = addHeader(doc, item.name || "Ingredient");

  // Fields
  y = printField(doc, "Brand", item.brand_name, y);
  y = printField(doc, "Supplier", item.supplier_name, y);
  y = printField(doc, "Category", item.category_name, y);
  y = printField(doc, "Unit", item.unit, y);
  y = printField(doc, "Price per Unit", fmt(item.price_per_unit, 4), y);
  if (item.package_price) y = printField(doc, "Package Price", fmt(item.package_price), y);
  if (item.package_size) y = printField(doc, "Package Size", fmt(item.package_size), y);
  if (item.sku) y = printField(doc, "SKU", item.sku, y);
  if (item.notes) y = printField(doc, "Notes", item.notes, y);
  y = printField(doc, "Active", item.is_active ? "Yes" : "No", y);

  addFooter(doc);
  openPdf(doc);
}

// ── Ingredients List PDF ──────────────────────────────────────────────────

/**
 * Generate a PDF table listing all ingredients.
 * @param {Array} items - Array of ingredient objects
 */
export async function exportIngredientsListPdf(items) {
  if (!items?.length) return;

  const doc = await createDoc(true);
  let y = addHeader(doc, "Ingredients");

  const head = [["Name", "Brand", "Supplier", "Category", "Unit", "Price/Unit", "SKU"]];
  const body = items.map((i) => [
    i.name || "",
    i.brand_name || "-",
    i.supplier_name || "-",
    i.category_name || "-",
    i.unit || "",
    fmt(i.price_per_unit, 4),
    i.sku || "-",
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head,
    body,
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [97, 97, 97], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
    columnStyles: { 5: { halign: "right" } },
  });

  addFooter(doc);
  openPdf(doc);
}

// ── Packaging List PDF ───────────────────────────────────────────────────

/**
 * Generate a PDF table listing all packaging items.
 * @param {Array} items - Array of packaging objects
 */
export async function exportPackagingListPdf(items) {
  if (!items?.length) return;

  const doc = await createDoc(true);
  let y = addHeader(doc, "Packaging");

  const head = [["Name", "Brand", "Supplier", "Unit", "Price/Unit", "Pkg Price", "Pkg Size", "SKU"]];
  const body = items.map((i) => [
    i.name || "",
    i.brand_name || "-",
    i.supplier_name || "-",
    i.unit || "",
    fmt(i.price_per_unit, 4),
    i.package_price ? fmt(i.package_price) : "-",
    i.package_size ? fmt(i.package_size) : "-",
    i.sku || "-",
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head,
    body,
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [97, 97, 97], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
    columnStyles: { 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } },
  });

  addFooter(doc);
  openPdf(doc);
}

// ── Packaging Detail PDF ──────────────────────────────────────────────────

/**
 * Generate a detail PDF for a single packaging item.
 * @param {Object} item - Packaging object with all fields
 */
export async function exportPackagingDetailPdf(item) {
  if (!item) return;

  const doc = await createDoc();
  let y = addHeader(doc, item.name || "Packaging Item");

  // Fields
  y = printField(doc, "Brand", item.brand_name, y);
  y = printField(doc, "Supplier", item.supplier_name, y);
  y = printField(doc, "Unit", item.unit, y);
  y = printField(doc, "Price per Unit", fmt(item.price_per_unit, 4), y);
  if (item.package_price) y = printField(doc, "Package Price", fmt(item.package_price), y);
  if (item.package_size) y = printField(doc, "Package Size", fmt(item.package_size), y);
  if (item.sku) y = printField(doc, "SKU", item.sku, y);
  y = printField(doc, "Active", item.is_active ? "Yes" : "No", y);

  addFooter(doc);
  openPdf(doc);
}

// ── Combo-specific food cost ─────────────────────────────────────────────

/**
 * Whether a recipe/packaging line applies to the given (brand, location):
 * a NULL on the line means "applies to all".
 */
function lineApplies(line, brandId, locationId) {
  if (line.brand_id != null && line.brand_id !== brandId) return false;
  if (line.location_id != null && line.location_id !== locationId) return false;
  return true;
}

/**
 * Food cost for a selling price row.
 *
 * If sp.brand_id is set, the backend already attached the right per-(brand,
 * location) food_cost — use it directly. If the row was synthesized by
 * expandSellingPrices (brand_id assigned client-side from a brand-NULL price),
 * sp.food_cost is for the NULL-brand fallback and would understate brand-
 * specific packaging/recipe lines, so we recompute for the expanded brand.
 */
function comboFoodCost(item, sp) {
  const wasExpanded = sp.__expanded === true;
  if (!wasExpanded && sp.food_cost != null) return Number(sp.food_cost);
  // Recompute for the expanded (brand, location) using recipe_lines and packaging
  const brandId = sp.brand_id || null;
  const locationId = sp.location_id || null;
  let recipeCost = 0;
  for (const rl of (item.recipe_lines || [])) {
    if (!lineApplies(rl, brandId, locationId)) continue;
    recipeCost += Number(rl.quantity || 0) * Number(rl.recipe_cost || 0);
  }
  let pkgCost = 0;
  for (const pl of (item.packaging || [])) {
    if (!lineApplies(pl, brandId, locationId)) continue;
    pkgCost += Number(pl.quantity || 0) * Number(pl.price_per_unit || 0);
  }
  return recipeCost + pkgCost;
}

/**
 * Expand selling prices into per-brand rows when a selling price has no brand
 * but packaging lines have brand-specific costs.
 * E.g., one selling price (brand=null, price=61) with packaging for Felice and Little Italy
 * becomes two rows: one for Felice packaging cost, one for Little Italy packaging cost.
 * @param {Array} prices - Selling prices array
 * @param {Array} packaging - Packaging lines array
 * @returns {Array} Expanded selling prices (may be larger than input)
 */
function expandSellingPrices(prices, packaging) {
  if (!packaging?.length) return prices;
  const expanded = [];
  for (const sp of prices) {
    // If selling price already has a brand, keep as-is
    if (sp.brand_id) {
      expanded.push(sp);
      continue;
    }
    // Collect distinct brands from packaging lines (excluding null-brand lines)
    const pkgBrands = new Map();
    for (const p of packaging) {
      if (p.brand_id && !pkgBrands.has(p.brand_id)) {
        pkgBrands.set(p.brand_id, p.brand_name);
      }
    }
    // If packaging has brand-specific lines, expand into one row per brand.
    // Mark __expanded so comboFoodCost knows to recompute (sp.food_cost from
    // backend was for brand=NULL and would miss brand-specific lines).
    if (pkgBrands.size > 1) {
      for (const [brandId, brandName] of pkgBrands) {
        expanded.push({ ...sp, brand_id: brandId, brand_name: brandName, __expanded: true });
      }
    } else {
      expanded.push(sp);
    }
  }
  return expanded;
}

// ── Menu Items List PDF ──────────────────────────────────────────────────

/**
 * Generate a landscape PDF table listing all menu items.
 * Each menu item spans multiple rows — one per selling price combination
 * (platform/brand/location), with food cost recalculated per combo
 * (recipe cost + matching packaging lines).
 * Items with no selling prices get a single row with total food cost.
 * The item name is only shown on the first row of each group.
 *
 * @param {Array} items - Array of menu item objects (each with selling_prices and packaging arrays from detail fetch)
 */
export async function exportMenuItemsListPdf(items) {
  if (!items?.length) return;

  const doc = await createDoc(true);
  let y = addHeader(doc, "Menu Items");

  // Group items by category
  const grouped = {};
  for (const item of items) {
    const cat = item.category_name || "Uncategorized";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  }

  const head = [["Name", "Size", "Platform", "Brand", "Location", "Food Cost", "Price", "FC%"]];
  const body = [];

  // Build table body with category separator rows
  for (const category of Object.keys(grouped)) {
    // Category header row spanning all columns
    body.push([{ content: category, colSpan: 8, styles: { fontStyle: "bold", fillColor: [230, 230, 230], fontSize: 8 } }]);

    for (const item of grouped[category]) {
      // Expand selling prices: if a price has no brand but packaging has brand-specific
      // lines, split into one row per packaging brand to show per-brand food cost
      const prices = expandSellingPrices(item.selling_prices || [], item.packaging);
      if (prices.length === 0) {
        // No selling prices → no (brand, location) context, so no food cost
        body.push([
          item.name || "",
          item.size || "-",
          "-", "-", "-",
          "-",
          "-", "-",
        ]);
      } else {
        // One row per selling price — recalculate food cost per combo
        prices.forEach((sp, idx) => {
          const fc = comboFoodCost(item, sp);
          const fcPct = sp.price && fc
            ? ((fc / sp.price) * 100).toFixed(1) + "%"
            : "-";
          body.push([
            idx === 0 ? (item.name || "") : "",
            idx === 0 ? (item.size || "-") : "",
            sp.platform_name || "All",
            sp.brand_name || "All",
            sp.location_name || "All",
            fmt(fc),
            fmt(sp.price),
            fcPct,
          ]);
        });
      }
    }
  }

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head,
    body,
    theme: "grid",
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [97, 97, 97], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7 },
    columnStyles: {
      5: { halign: "right" },  // Food Cost
      6: { halign: "right" },  // Price
      7: { halign: "right" },  // FC%
    },
  });

  addFooter(doc);
  openPdf(doc);
}


// ── Menu Items without prices (2026-10-30) ───────────────────────────────

/**
 * The menu items on screen (filters applied) as a PDF with NO prices or costs —
 * name, size, description in English and Arabic, grouped by menu category.
 * Made to be shared (e.g. the catering list for a client), so no "internal" watermark.
 * @param {Array} items - Menu items as listed (GET /api/menu/menu-items)
 * @param {Object} opts
 * @param {string} opts.title - Document title ("Catering menu", "Menu items")
 * @param {string[]} [opts.filters] - Active filters, printed under the title
 * @param {{types: string[]}|null} [opts.groupBy] - group by catering type (live station / buffet)
 *   in this order instead of by menu category; an item of several types is listed under each,
 *   an item of none under "No catering type"
 */
export async function exportMenuItemsNoPricesPdf(items, { title, filters = [], groupBy = null }) {
  if (!items?.length) return;

  const doc = await createDoc(true);
  let y = addHeader(doc, title);
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text([`${items.length} items`, ...filters].join(" · "), MARGIN, y);
  doc.setTextColor(0, 0, 0);
  y += 4;

  // Group by menu category, in the list's own order (category sort order, then name), or by
  // catering type for a catering menu
  const grouped = {};
  if (groupBy) {
    for (const t of groupBy.types) grouped[t] = [];
    for (const item of items) {
      const types = (item.catering_types || []).filter((t) => groupBy.types.includes(t));
      if (!types.length) (grouped["No catering type"] ||= []).push(item);
      for (const t of types) grouped[t].push(item);
    }
    for (const k of Object.keys(grouped)) if (!grouped[k].length) delete grouped[k];
  } else {
    for (const item of items) {
      const cat = item.category_name || "Uncategorized";
      (grouped[cat] ||= []).push(item);
    }
  }
  const body = [];
  // A catering section is titled by its station ("Pizza station") or "Buffet"
  const sectionTitle = (k) => (groupBy && groupBy.types.includes(k) && k !== "Buffet" ? `${k} station` : k);
  for (const [category, rows] of Object.entries(grouped)) {
    body.push([{ content: sectionTitle(category), colSpan: 5, styles: { fontStyle: "bold", fillColor: [230, 230, 230], fontSize: 8 } }]);
    for (const item of rows) {
      body.push([item.name || "", item.size || "", item.description || "", item.name_ar || "", item.description_ar || ""]);
    }
  }

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head: [["Item", "Size", "Description", "Arabic name", "Arabic description"]],
    body,
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 1.5, valign: "top" },
    headStyles: { fillColor: [97, 97, 97], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 48 },
      1: { cellWidth: 18 },
      2: { cellWidth: 80 },
      3: { cellWidth: 45, halign: "right" },
    },
    // Arabic cells use the Amiri font (Helvetica has no Arabic glyphs)
    didParseCell: (data) => {
      if (data.section === "body" && hasArabic(data.cell.raw)) {
        data.cell.styles.font = "Amiri";
        data.cell.styles.halign = "right";
      }
    },
  });

  // Page numbers and date only — no confidential watermark on a list meant to be shared
  const totalPages = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(130, 130, 130);
    doc.setFont("helvetica", "normal");
    doc.text(`Page ${i} / ${totalPages}`, pageWidth - MARGIN, pageHeight - 6, { align: "right" });
  }
  doc.setTextColor(0, 0, 0);
  openPdf(doc);
}
